// A geographic terrain-elevation sampler for the wind layers' masking. Prefetches
// terrain DEM grids over a lng/lat box at a map-zoom-matched zoom, then answers
// aboveLevel(lng,lat,maskLevel) by point. Decoupling the terrain zoom from each
// layer's DATA zoom is what keeps streams/fill/barbs consistent — they all read
// the same true terrain (the Drvar bug was each sampling a different zoom).
import { decodeTerrainGrid, terrainAboveAtFrac } from './terrainMask';
import { terrainTileTemplate } from './urls';

// Terrain zoom tracks the map zoom (resolve real terrain where the user looks),
// clamped so a viewport never needs an unreasonable number of tiles.
export const terrainMaskZoom = (mapZoom) => Math.max(6, Math.min(11, Math.round(mapZoom)));

const tileFrac = (lng, lat, z) => {
  const n = Math.pow(2, z);
  const fx = ((lng + 180) / 360) * n;
  const fy = ((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * n;
  const x = Math.floor(fx), y = Math.floor(fy);
  return { x, y, fracX: fx - x, fracY: fy - y };
};

// getTile(url, signal) -> decoded {w,h,data} | null  (inject tileCache.getDecodedTile).
// Returns { aboveLevel(lng,lat,maskLevel) }. Region too large → no-op (never masks).
export async function buildTerrainSampler({ getTile, base, z, west, south, east, north, signal, maxTiles = 36 }) {
  const tl = tileFrac(west, north, z);
  const br = tileFrac(east, south, z);
  const xMin = Math.min(tl.x, br.x), xMax = Math.max(tl.x, br.x);
  const yMin = Math.min(tl.y, br.y), yMax = Math.max(tl.y, br.y);
  const grids = new Map();
  if ((xMax - xMin + 1) * (yMax - yMin + 1) <= maxTiles) {
    const tpl = terrainTileTemplate(base);
    await Promise.all(
      Array.from({ length: (xMax - xMin + 1) * (yMax - yMin + 1) }, (_, k) => {
        const cols = xMax - xMin + 1;
        const x = xMin + (k % cols), y = yMin + Math.floor(k / cols);
        const url = tpl.replace('{z}', z).replace('{x}', x).replace('{y}', y);
        return getTile(url, signal)
          .then((t) => { if (t) grids.set(`${x}/${y}`, decodeTerrainGrid(t)); })
          .catch(() => {});
      }),
    );
  }
  return {
    aboveLevel(lng, lat, maskLevel) {
      if (maskLevel == null) return false;
      const f = tileFrac(lng, lat, z);
      const g = grids.get(`${f.x}/${f.y}`);
      if (!g) return false; // outside prefetched region → don't mask
      return terrainAboveAtFrac(g, f.fracX, f.fracY, maskLevel);
    },
  };
}
